import React, { type ReactNode, useMemo } from 'react';
import type {
	EnhancedUFOInteractionContextType,
	ReactProfilerTiming,
} from '@atlaskit/react-ufo/common';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';
import { fg } from '@atlassian/jira-feature-gating';

const NoopSsrRenderProfiler = ({
	children,
}: {
	children?: ReactNode | undefined;
	labelStack: ReactProfilerTiming['labelStack'];
	onRender: EnhancedUFOInteractionContextType['onRender'];
}) => {
	return <>{children}</>;
};

// These are stored outside react context to be resilient to concurrent mode
// restarting the start marker from a suspense and losing the initial render
const startTimes: Map<string, number[]> = new Map();
// Keep track of the last interaction id and reset the start timers if it ever changes.
// This is to allow multi-step ssr to track the render timers from different "interaction"s seperately
let lastActiveInteraction: string | undefined;
function checkActiveInteractionAndResetStartMarksIfSet() {
	const activeInteractionId = getActiveInteraction()?.id;
	if (!!lastActiveInteraction && lastActiveInteraction !== activeInteractionId) {
		startTimes.clear();
	}
	lastActiveInteraction = activeInteractionId;
}
const onStartRender = (id: string) => {
	if (!startTimes.has(id)) {
		startTimes.set(id, []);
	}

	startTimes.get(id)?.push(performance.now());
};

const ProfilerMarker = ({ onRender }: { onRender?: () => void }) => {
	if (__SERVER__) {
		onRender?.();
	}
	return null;
};

// Exported for easier testing, just rename this to SsrRenderProfiler and make default export when cleaning jira-ssr-enable-ufo-in-ssr
export const SsrRenderProfilerFGOn = ({
	children,
	labelStack,
	onRender,
}: {
	children?: ReactNode | undefined;
	labelStack: ReactProfilerTiming['labelStack'];
	onRender: EnhancedUFOInteractionContextType['onRender'];
}) => {
	const reactProfilerId = useMemo(() => labelStack.map((l) => l.name).join('/'), [labelStack]);

	checkActiveInteractionAndResetStartMarksIfSet();
	return (
		<>
			<ProfilerMarker onRender={() => onStartRender(reactProfilerId)} />
			{children}
			<ProfilerMarker
				onRender={() => {
					const startTimesForId = startTimes.get(reactProfilerId);
					if (startTimesForId && startTimesForId.length) {
						const endTime = performance.now();
						const firstStartTime = startTimesForId[0];
						const lastStartTime = startTimesForId[startTimesForId.length - 1];
						const baseDuration = endTime - lastStartTime;
						const actualDuration = endTime - firstStartTime;
						onRender(
							'mount', // this is incorrect, but on the server there is no mount phase
							actualDuration,
							baseDuration,
							firstStartTime,
							endTime,
						);
					}
				}}
			/>
		</>
	);
};

const SsrRenderProfiler = (props: Parameters<typeof SsrRenderProfilerFGOn>[0]) => {
	if (__SERVER__) {
		return fg('jira-ssr-enable-ufo-in-ssr') ? (
			<SsrRenderProfilerFGOn {...props} />
		) : (
			<NoopSsrRenderProfiler {...props} />
		);
	}

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('jfp_magma_jira-use-id-sync') && fg('jira-ssr-enable-ufo-in-ssr')) {
		// ensure structure similar to SSR implementation
		return (
			<>
				<ProfilerMarker />
				{props.children}
			</>
		);
	}
	return <>{props.children}</>;
};
export default SsrRenderProfiler;
